/**
 * EditProfileImageSection
 *
 * The tappable profile-image block at the top of `EditUserProfilePage` — a port
 * of Flutter's `profileIcon()`
 * (`lib/presentation/screens/user/edit_user_profile_screen.dart:586-651`).
 *
 * Structure (Flutter `:587-647`): one `GestureDetector` wraps BOTH the circle
 * and the caption below it, so the whole block is a single tap target that
 * calls `onChangeImageTapped` (`:589-591`). Web uses a `<button>` for that
 * target so it is keyboard-reachable.
 *
 * The circle mirrors `CircleBorderAvatar` (`circle_border_avatar.dart:36-83`)
 * at this call site's defaults (`borderWidth: 0`, `borderColor: transparent`,
 * `useSkeletonAnimation: false` — the call site at `:600-604` passes none of
 * them), which reduces to three render branches:
 *   - `image == null`   → a plain `backgroundColorLight` circle (`:36-45`).
 *   - `startsWith(http)` → `CachedNetworkImage`, `BoxFit.cover` (`:61-74`).
 *   - otherwise          → `Image.file`, `BoxFit.cover` (`:75-80`) — on web the
 *     freshly-picked `blob:` object URL, rendered before it is ever uploaded.
 *
 * Overlay/caption parity notes:
 *   - The overlay is a centered "Choose profile image" TEXT label shown only
 *     while the image is null (`:605-627`). There is deliberately NO camera
 *     icon overlay — the task prompt's claim of one contradicts Flutter.
 *   - The caption below reads 'Change' only while an image is present
 *     (`:631-645`). Flutter's middle `!isEditing` branch ("You can always do it
 *     later…") is onboarding-only and structurally unreachable on web
 *     (`isEditing` is always true — `:401-403`, `:425-426`), so it is not ported.
 *
 * Presentation-only and stateless: state lives in `useEditUserProfile` and the
 * picker sheet is opened by the page.
 */

import React from 'react';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface EditProfileImageSectionProps {
  /** Current image: an `https://…` profiles-bucket URL, a `blob:` object URL for a freshly-picked local file, or null. */
  profileImage: string | null;
  /** Tap handler for the whole block — the page opens the AddContentSheet (Flutter `:589-591` → `onChangeImageTapped`). */
  onChangeImage: () => void;
}

export const EditProfileImageSection: React.FC<EditProfileImageSectionProps> = ({
  profileImage,
  onChangeImage,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  // Flutter `circle_border_avatar.dart:61`: the http/local split decides between
  // the cached-network arm and the local-file arm. A `blob:` object URL fails
  // `startsWith('http')` and therefore takes the local arm, exactly as a picked
  // file does on Flutter.
  const isRemoteImage = profileImage !== null && profileImage.startsWith('http');

  const imageStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: responsive.spacing.lg,
        paddingBottom: responsive.spacing['2xl'],
      }}
    >
      {/* One tap target spanning circle + caption (Flutter's single GestureDetector, `:589-647`). */}
      <button
        type="button"
        onClick={onChangeImage}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          background: theme.transparent,
          border: 'none',
          padding: 0,
          cursor: 'pointer',
        }}
        {...qaAttr({ id: 'edit-profile-image', value: profileImage ? 'set' : 'empty' })}
      >
        <div
          style={{
            position: 'relative',
            width: responsive.avatar.xlarge,
            height: responsive.avatar.xlarge,
            borderRadius: responsive.borderRadius.round,
            backgroundColor: theme.surface,
            overflow: 'hidden',
          }}
        >
          {/*
            `profileImage === null` renders nothing here: the themed circle IS
            the empty state (`circle_border_avatar.dart:36-45`).
          */}
          {profileImage !== null &&
            (isRemoteImage ? (
              <CachedImage src={profileImage} alt="" style={imageStyle} />
            ) : (
              /*
                The `Image.file` arm (`:75-80`). Deliberately NOT routed through
                `CachedImage`: its URL-keyed metadata cache is meaningless for a
                per-pick, single-use object URL.
              */
              <img src={profileImage} alt="" style={imageStyle} />
            ))}

          {/*
            Overlay label, only while the image is null (Flutter `:609-625`).
            Mirrors `Positioned.fill` → `Align.center` → `Padding(all: 8)` →
            centered text. `pointerEvents: none` keeps the tap on the button.
          */}
          {profileImage === null && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: responsive.spacing.xs,
                pointerEvents: 'none',
              }}
            >
              <span
                style={{
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  overflow: 'hidden',
                  color: theme.text.secondary,
                  fontSize: responsive.font.xs,
                  fontWeight: responsive.fontWeight.regular,
                  textAlign: 'center',
                }}
              >
                {l10n.editUserProfile.chooseProfileImage}
              </span>
            </div>
          )}
        </div>

        {/* Caption below the circle, only while an image is present (Flutter `:631-645`). */}
        {profileImage !== null && (
          <span
            style={{
              marginTop: responsive.spacing.xs,
              color: theme.text.secondary,
              fontSize: responsive.font.xs,
              textAlign: 'center',
            }}
          >
            {l10n.editUserProfile.changeProfileImage}
          </span>
        )}
      </button>
    </div>
  );
};
